import i18n from '@/i18n'
import OrganizationRepository from '@/js/api/OrganizationRepository'
import { FeatureAreaKey } from '@/domains/website/featureCatalogue'

// =============================================================================
// PRICING CONFIGURATION
// Central place for all pricing plans, features, and display data
// =============================================================================

// -----------------------------------------------------------------------------
// PLAN LIMITS
// Plans differ only by these three limits (Ansis, 2026-10-25). Every plan has every feature —
// the list the website shows is src/domains/website/featureCatalogue.js. The limits define the
// plans on the website; the platform does not enforce them yet (it will).
// -----------------------------------------------------------------------------
export const PlanLimitKey = {
  EVENTS_PER_WEEK: 'eventsPerWeek',
  PARTICIPANTS_PER_EVENT: 'participantsPerEvent',
  TRAINERS: 'trainers'
}

const PLAN_LIMIT_LABEL_KEYS = {
  [PlanLimitKey.EVENTS_PER_WEEK]: 'pages.pricing.features.events-per-week',
  [PlanLimitKey.PARTICIPANTS_PER_EVENT]: 'pages.pricing.features.participants-per-event',
  [PlanLimitKey.TRAINERS]: 'pages.pricing.features.number-of-instructors'
}

/**
 * Feature areas the Starter plan does not promise on the website (Ansis, 2026-10-26): a trainer who
 * needs payments, passes or the client register takes Active. Not enforced in the platform.
 */
export const STARTER_EXCLUDED_FEATURE_AREAS = [FeatureAreaKey.CLIENTS, FeatureAreaKey.PAYMENTS, FeatureAreaKey.PASSES]

/**
 * Yes/no rows of the plan comparison table, after the limits. A plan includes a row unless it lists
 * it in `excludedFeatures`. Website terms only — nothing here is enforced in the platform.
 */
export const PlanFeatureRow = {
  PAID_AREAS: 'paidAreas',
  SEARCH_INDEXING: 'searchIndexing',
  MONTHLY_REPORT: 'monthlyReport'
}

const PLAN_FEATURE_ROW_LABEL_KEYS = {
  [PlanFeatureRow.PAID_AREAS]: 'pages.pricing.features.paid-areas',
  [PlanFeatureRow.SEARCH_INDEXING]: 'pages.pricing.features.search-indexing',
  [PlanFeatureRow.MONTHLY_REPORT]: 'pages.pricing.features.monthly-report'
}

/** Starter: payments, passes, client register, Google search and the monthly e-mail start with Active (Ansis, 2026-10-26). */
const STARTER_EXCLUDED_FEATURES = Object.values(PlanFeatureRow)

/** @returns {Array<{ key: string, label: string }>} the yes/no rows, in display order */
export const getPlanFeatureRows = () => Object.values(PlanFeatureRow).map(key => ({
  key,
  label: i18n.t(PLAN_FEATURE_ROW_LABEL_KEYS[key])
}))

/** @returns {boolean} whether the plan includes a PlanFeatureRow */
export const planIncludesFeature = (plan, rowKey) => !(plan.excludedFeatures || []).includes(rowKey)

/** A limit with no ceiling. */
const UNLIMITED = 'unlimited'

/**
 * @param {Object} values a formatted value per PlanLimitKey the plan is limited by; a key left out is
 *   not shown (individual plans have no trainer row — the plan name already says one trainer)
 * @returns {Array<{ key: string, label: string, value: string }>} in display order
 */
const planLimits = (values) => Object.values(PlanLimitKey)
  .filter(key => key in values)
  .map(key => ({
    key,
    label: i18n.t(PLAN_LIMIT_LABEL_KEYS[key]),
    value: values[key]
  }))

// -----------------------------------------------------------------------------
// PLAN PRICING CONFIGURATIONS
// -----------------------------------------------------------------------------

export const PRICING_PLANS = {
  individual: {
    starter: 'starter',
    active: 'active',
    pro: 'pro'
  },
  organization: {
    team: 'team',
    growth: 'growth',
    enterprise: 'enterprise'
  }
}

/** "Līdz 20", "Neierobežoti" or "Pēc vienošanās". */
const formatLimit = (value) => {
  if (value === UNLIMITED) {
    return i18n.t('pricing-plans.common.limits.unlimited')
  }
  if (typeof value === 'string' && value.toLowerCase() === 'custom') {
    return i18n.t('pricing-plans.common.limits.custom')
  }
  return i18n.t('pricing-plans.common.limits.up-to', { count: value })
}


export const getPlanConfigs = () => ({
  // Individual Trainer Plans
  individual: {
    starter: {
      id: PRICING_PLANS.individual.starter,
      icon: 'mdi-human-greeting',
      title: i18n.t('pricing-plans.plans.individual.starter.title'),
      price: '9€',
      period: i18n.t('pricing-plans.common.period'),
      description: i18n.t('pricing-plans.plans.individual.starter.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.start-using', { plan: i18n.t('pricing-plans.plans.individual.starter.title') }),
      planType: OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL,
      excludedFeatureAreas: STARTER_EXCLUDED_FEATURE_AREAS,
      excludedFeatures: STARTER_EXCLUDED_FEATURES,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(2),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(10)
      })
    },
    
    active: {
      id: PRICING_PLANS.individual.active,
      icon: 'mdi-meditation',
      title: i18n.t('pricing-plans.plans.individual.active.title'),
      price: '15€',
      period: i18n.t('pricing-plans.common.period'),
      description: i18n.t('pricing-plans.plans.individual.active.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.start-using', { plan: i18n.t('pricing-plans.plans.individual.active.title') }),
      planType: OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(5),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(20)
      })
    },

    pro: {
      id: PRICING_PLANS.individual.pro,
      title: i18n.t('pricing-plans.plans.individual.pro.title'),
      price: '39€',
      period: i18n.t('pricing-plans.common.period'),
      description: i18n.t('pricing-plans.plans.individual.pro.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.start-using', { plan: i18n.t('pricing-plans.plans.individual.pro.title') }),
      planType: OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(20),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(50)
      })
    }
  },

  // Organization Plans
  organization: {
    team: {
      id: PRICING_PLANS.organization.team,
      icon: 'mdi-account-group',
      title: i18n.t('pricing-plans.plans.organization.team.title'),
      price: '49€',
      period: i18n.t('pricing-plans.common.period'),
      description: i18n.t('pricing-plans.plans.organization.team.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.start-using', { plan: i18n.t('pricing-plans.plans.organization.team.title') }),
      planType: OrganizationRepository.ORGANIZATION_TYPE_STUDIO,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(20),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(10),
        [PlanLimitKey.TRAINERS]: formatLimit(3)
      })
    },

    growth: {
      id: PRICING_PLANS.organization.growth,
      icon: 'mdi-office-building',
      title: i18n.t('pricing-plans.plans.organization.growth.title'),
      price: '129€',
      period: i18n.t('pricing-plans.common.period'),
      description: i18n.t('pricing-plans.plans.organization.growth.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.start-using', { plan: i18n.t('pricing-plans.plans.organization.growth.title') }),
      planType: OrganizationRepository.ORGANIZATION_TYPE_STUDIO,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(40),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(20),
        [PlanLimitKey.TRAINERS]: formatLimit(10)
      })
    },

    enterprise: {
      id: PRICING_PLANS.organization.enterprise,
      icon: 'mdi-city',
      title: i18n.t('pricing-plans.plans.organization.enterprise.title'),
      price: i18n.t('pricing-plans.plans.organization.enterprise.price'),
      period: '',
      description: i18n.t('pricing-plans.plans.organization.enterprise.description'),
      isFeatured: false,
      ctaText: i18n.t('pricing-plans.common.cta-templates.contact-for-price'),
      planType: OrganizationRepository.ORGANIZATION_TYPE_STUDIO,
      // Plan limits — defined, not yet enforced
      limits: planLimits({
        [PlanLimitKey.EVENTS_PER_WEEK]: formatLimit(UNLIMITED),
        [PlanLimitKey.PARTICIPANTS_PER_EVENT]: formatLimit(UNLIMITED),
        [PlanLimitKey.TRAINERS]: formatLimit(UNLIMITED)
      })
    }
  }
})

// -----------------------------------------------------------------------------
// GENERATED ARRAYS FOR BACKWARD COMPATIBILITY
// -----------------------------------------------------------------------------

// Individual Trainer Plans (for existing components)
export const getIndividualTrainerPlans = () => {
  const planConfigs = getPlanConfigs()
  return [
    planConfigs.individual.starter,
    planConfigs.individual.active,
    planConfigs.individual.pro
  ]
}

// Organization Plans (for existing components)
export const getOrganizationPlans = () => {
  const planConfigs = getPlanConfigs()
  return [
    planConfigs.organization.team,
    planConfigs.organization.growth,
    planConfigs.organization.enterprise
  ]
}

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------

/**
 * Returns OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL or OrganizationRepository.ORGANIZATION_TYPE_STUDIO for a given plan identifier, or null if unknown
 */
export function getAccountTypeByPlanId(planId) {
  if (!planId) return null
  const id = String(planId).toLowerCase()
  const plans = getPlanConfigs()
  
  // Search through all plans to find the one with matching ID
  for (const category of Object.values(plans)) {
    for (const plan of Object.values(category)) {
      if (plan.id === id) {
        // Use the planType field from the plan configuration
        if (plan.planType === OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL) return OrganizationRepository.ORGANIZATION_TYPE_INDIVIDUAL
        if (plan.planType === OrganizationRepository.ORGANIZATION_TYPE_STUDIO) return OrganizationRepository.ORGANIZATION_TYPE_STUDIO
        return null
      }
    }
  }
  
  return null
}

/**
 * Returns plan title by plan ID for display purposes
 */
export function getPlanTitleById(planId) {
  if (!planId) return null
  const id = String(planId).toLowerCase()
  const plans = getPlanConfigs()
  
  // Search through all plans to find the one with matching ID
  for (const category of Object.values(plans)) {
    for (const plan of Object.values(category)) {
      if (plan.id === id) {
        return plan.title
      }
    }
  }
  
  return null
}

/**
 * The plan config for a stored `payment_plan` id, or null when none matches (no plan chosen, or a
 * legacy id). Searches every account-type category.
 * @param {string|null|undefined} planId
 */
export function findPlanById (planId) {
  if (!planId) return null
  for (const category of Object.values(getPlanConfigs())) {
    for (const plan of Object.values(category)) {
      if (plan.id === planId) return plan
    }
  }
  return null
}
